<script lang="ts">
  import type { DerivedWorkout } from "../../lib/lapAnalysis";
  import { fmtPaceTick } from "../../lib/format";
  import { COLORS } from "../../lib/designTokens";

  interface Props { dw: DerivedWorkout }
  let { dw }: Props = $props();

  // Derive a 1-10 "effort score" from HR distribution (% of workout above 85% peak)
  let effortScore = $derived.by(() => {
    if (!dw.maxHr || dw.hrSeries.length === 0) return null;
    const threshold = dw.maxHr * 0.85;
    const above = dw.hrSeries.filter((p) => p.bpm >= threshold).length;
    const pct = above / dw.hrSeries.length;
    // Map % time above threshold to 1-10 effort scale
    return Math.min(10, Math.max(1, Math.round(pct * 12 + 3)));
  });

  let effortLabel = $derived.by(() => {
    const e = effortScore;
    if (e === null) return "—";
    if (e >= 9) return "All Out";
    if (e >= 7) return "Hard";
    if (e >= 5) return "Tempo";
    if (e >= 3) return "Aerobic";
    return "Easy";
  });

  let totalStrokes = $derived(dw.laps.reduce((s, l) => s + (l.strokeCount ?? 0), 0));
  let avgPaceSec = $derived(
    dw.totalDistanceMeters > 0
      ? (dw.totalDurationSeconds / dw.totalDistanceMeters) * 100
      : null,
  );
</script>

<article class="proto">
  <header>
    <span class="num">04</span>
    <h2>Effort Hero</h2>
    <p class="story">
      One screen that answers: <em>how hard was this swim?</em>
      Effort score derived from time spent in the top 15% of your peak HR.
    </p>
  </header>

  <div class="hero">
    <div class="effort">
      <div class="big">{effortScore ?? "—"}</div>
      <div class="big-label">{effortLabel}</div>
      <div class="ladder">
        {#each Array(10) as _, i}
          <span class="bar" class:on={effortScore !== null && i < effortScore}></span>
        {/each}
      </div>
    </div>

    <div class="stats">
      <div class="stat">
        <span class="k">Avg pace</span>
        <span class="v" style:color={COLORS.blueGreen}>{fmtPaceTick(avgPaceSec)}<small> /100m</small></span>
      </div>
      <div class="stat">
        <span class="k">Distance</span>
        <span class="v" style:color={COLORS.skyBlue}>{dw.totalDistanceMeters.toFixed(0)}<small> m</small></span>
      </div>
      <div class="stat">
        <span class="k">Strokes</span>
        <span class="v" style:color={COLORS.swimmerBlue}>{totalStrokes.toFixed(0)}<small> total</small></span>
      </div>
      <div class="stat">
        <span class="k">Avg HR</span>
        <span class="v" style:color={COLORS.coralRed}>{dw.avgHr?.toFixed(0) ?? "—"}<small> bpm</small></span>
      </div>
    </div>
  </div>
</article>

<style>
  .proto { background: var(--bg-card); border-radius: 12px; padding: 1rem; margin: 0.75rem 0; }
  header { margin-bottom: 1rem; }
  .num { display: inline-block; padding: 0.1rem 0.5rem; background: var(--accent-primary); color: var(--bg-canvas); font-size: 0.7rem; font-weight: 700; border-radius: 3px; letter-spacing: 1px; }
  h2 { font-size: 1.1rem; font-weight: 700; margin: 0.4rem 0 0.2rem; }
  .story { font-size: 0.78rem; color: var(--text-muted); margin: 0; }

  .hero { display: grid; grid-template-columns: auto 1fr; gap: 1.5rem; align-items: center; padding: 0.5rem 0; }
  .effort { text-align: center; padding: 0.5rem 0; }
  .big { font-size: 4rem; font-weight: 800; line-height: 1; color: var(--accent-fire); letter-spacing: -0.04em; font-variant-numeric: tabular-nums; }
  .big-label { font-size: 0.85rem; font-weight: 700; color: var(--accent-fire); text-transform: uppercase; letter-spacing: 1.5px; margin-top: 0.25rem; }
  .ladder { display: flex; gap: 2px; justify-content: center; margin-top: 0.6rem; }
  .ladder .bar { width: 6px; height: 14px; background: var(--bg-raised); border-radius: 1px; }
  .ladder .bar.on { background: var(--accent-fire); }

  .stats { display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem 1rem; }
  .stat { display: flex; flex-direction: column; }
  .stat .k { font-size: 0.7rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 1px; }
  .stat .v { font-size: 1.4rem; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1.1; }
  .stat .v small { font-size: 0.7rem; color: var(--text-muted); font-weight: 400; margin-left: 0.2rem; }
</style>
